// Defaults, presets and small display helpers for the J1939 Frame Decoder & DBC Generator.
// All ID breakdown, decoding, range flags, DBC generation and validation happens in the
// backend (tools/j1939_frame_decoder_tool.py); this file only builds the request and labels
// things.

import { CATEGORY_LABELS as CAN_CATEGORY_LABELS } from './canFrameDecoder';

export { parseHexBytes, formatBytes } from './canFrameDecoder';

export const MAX_FRAMES = 32; // mirrors MAX_FRAMES in the backend
export const MAX_SPNS = 64; // mirrors MAX_SPNS
export const MAX_PAYLOAD = 8;

let seq = 0;
const nextId = (kind) => `${kind}-${++seq}`;

export const blankSpn = (n) => ({
  name: `Param_${n}`,
  spn: '',
  startBit: '0',
  length: '8',
  byteOrder: 'intel',
  valueType: 'unsigned',
  factor: '1',
  offset: '0',
  min: '',
  max: '',
  unit: '',
});

// Presets. Position, length, resolution and offset of each SPN were checked against at least
// two independent public references (see the user guide, Section 5); the names are our own.
const ENGINE_SPEED = {
  name: 'EngineSpeed', spn: '190', startBit: '24', length: '16', byteOrder: 'intel', valueType: 'unsigned',
  factor: '0.125', offset: '0', min: '', max: '', unit: 'rpm',
};
const ACTUAL_TORQUE = {
  name: 'ActualEnginePercentTorque', spn: '513', startBit: '16', length: '8', byteOrder: 'intel', valueType: 'unsigned',
  factor: '1', offset: '-125', min: '', max: '', unit: '%',
};
const WHEEL_SPEED = {
  name: 'WheelBasedVehicleSpeed', spn: '84', startBit: '8', length: '16', byteOrder: 'intel', valueType: 'unsigned',
  factor: '0.00390625', offset: '0', min: '', max: '', unit: 'km/h',
};

export const EEC1_FRAME = {
  canId: '0x0CF00400', data: 'F0 7D FF 00 1A 7D FF FF', messageName: 'EEC1', transmitter: '',
  spns: [ENGINE_SPEED, ACTUAL_TORQUE],
};
export const CCVS_FRAME = {
  canId: '0x18FEF100', data: 'FF 00 32 FF FF FF FF FF', messageName: 'CCVS', transmitter: '',
  spns: [WHEEL_SPEED],
};

export const DEFAULT_FRAMES = [EEC1_FRAME];
export const EXAMPLE_FRAMES = [EEC1_FRAME, CCVS_FRAME];

export const withIds = (frames) => frames.map((f) => ({
  ...f, id: nextId('frame'), spns: f.spns.map((s) => ({ ...s, id: nextId('spn') })),
}));

export const newSpn = (existing) => ({ ...blankSpn(existing.length + 1), id: nextId('spn') });

export function newFrame(existing = []) {
  const n = existing.length + 1;
  return {
    id: nextId('frame'),
    canId: '0x18FF0000',
    data: '00 00 00 00 00 00 00 00',
    messageName: `PG_${n}`,
    transmitter: '',
    spns: [newSpn([])],
  };
}

export const toPayloadFrame = (f) => ({
  canId: f.canId, data: f.data, messageName: f.messageName, transmitter: f.transmitter,
  spns: f.spns.map(({ id, ...s }) => s),
});

export const CATEGORY_LABELS = { ...CAN_CATEGORY_LABELS, canIds: 'CAN IDs', j1939: 'J1939 attributes' };

// One colour per SPN row in the bit map (cycled). Each pairs a fill with readable text.
export const SPN_COLORS = [
  { bg: '#2563eb', fg: '#ffffff' },
  { bg: '#059669', fg: '#ffffff' },
  { bg: '#d97706', fg: '#ffffff' },
  { bg: '#7c3aed', fg: '#ffffff' },
  { bg: '#0891b2', fg: '#ffffff' },
  { bg: '#db2777', fg: '#ffffff' },
  { bg: '#65a30d', fg: '#ffffff' },
  { bg: '#475569', fg: '#ffffff' },
];
export const spnColor = (k) => SPN_COLORS[k % SPN_COLORS.length];

// Decode status -> badge style + label.
export const STATUS_STYLES = {
  valid: { label: 'Valid', cls: 'bg-emerald-600 text-white hover:bg-emerald-600' },
  invalid: { label: 'Invalid', cls: 'bg-destructive text-destructive-foreground hover:bg-destructive' },
  notAvailable: { label: 'Not available', cls: 'bg-slate-500 text-white hover:bg-slate-500' },
  error: { label: 'Error indicator', cls: 'bg-red-700 text-white hover:bg-red-700' },
  parameterSpecific: { label: 'Parameter-specific', cls: 'bg-amber-500 text-white hover:bg-amber-500' },
  reserved: { label: 'Reserved range', cls: 'bg-amber-600 text-white hover:bg-amber-600' },
};
